/**
 * "New workflow" dialog.
 *
 * Direction + canonical (model, version) must be chosen at creation —
 * they're immutable afterwards, because they constrain which node kinds
 * are placeable in the DAG.
 *
 * Canonical model+version are picked together via a single combobox
 * sourced from /canonical: there's no reason to type them by hand
 * (mismatches blow up at save time anyway) and the dropdown shows
 * status so the operator can avoid binding to a draft.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import * as React from "react";
import { useNavigate } from "react-router-dom";

import { TagsField } from "@/components/tags";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { canonicalApi } from "@/lib/api-canonical";
import {
  workflowsApi,
  type Direction,
  type WorkflowKind,
} from "@/lib/api-workflows";
import { cn } from "@/lib/cn";

export interface WorkflowCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Discriminator: 'canonical' (default) shows direction + canonical
   *  pickers; 'orchestration' hides them and creates a kind-orchestration
   *  workflow. The redirect URL after create depends on this too. */
  kind?: WorkflowKind;
}

const DIRECTION_OPTIONS: Array<{ value: Direction; label: string; hint: string }> = [
  {
    value: "inbound",
    label: "Inbound",
    hint: "External source → canonical (parse + validate)",
  },
  {
    value: "internal",
    label: "Internal",
    hint: "Canonical → canonical (enrich / split / aggregate)",
  },
  {
    value: "outbound",
    label: "Outbound",
    hint: "Canonical → external target (transform + dispatch)",
  },
];

export function WorkflowCreateDialog({
  open,
  onOpenChange,
  kind = "canonical",
}: WorkflowCreateDialogProps) {
  const qc = useQueryClient();
  const nav = useNavigate();
  // Kind is now CHOSEN in the dialog (the list page is unified). The
  // `kind` prop is just the initial selection.
  const [selectedKind, setSelectedKind] = React.useState<WorkflowKind>(kind);
  const isCanonical = selectedKind === "canonical";

  const [name, setName] = React.useState("");
  const [direction, setDirection] = React.useState<Direction>("inbound");
  // Single source of truth for the picker: "<model>|<version>" so we
  // can round-trip both fields through one Combobox value.
  const [canonicalKey, setCanonicalKey] = React.useState<string | null>(null);
  const [description, setDescription] = React.useState("");
  const [tags, setTags] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (open) {
      setSelectedKind(kind);
      setName("");
      setDirection("inbound");
      setCanonicalKey(null);
      setDescription("");
      setTags([]);
    }
  }, [open, kind]);

  // Load all known canonical schemas. We show every status (active,
  // draft, deprecated) but tag them so the operator picks deliberately;
  // tying a new workflow to a deprecated schema is a smell but not
  // strictly forbidden (existing workflows do it during migrations).
  const canonicalQuery = useQuery({
    queryKey: ["canonical", "all"],
    queryFn: () => canonicalApi.list({ page_size: 100 }),
    select: (p) => p.items,
    enabled: open,
    staleTime: 60_000,
  });
  const canonicalOptions = React.useMemo<ComboboxOption[]>(() => {
    const rows = canonicalQuery.data ?? [];
    // Newest version per model first; active before draft before deprecated.
    const order = { active: 0, draft: 1, deprecated: 2 } as const;
    return rows
      .slice()
      .sort((a, b) => {
        const t = a.canonical_type.localeCompare(b.canonical_type);
        if (t !== 0) return t;
        const s = order[a.status] - order[b.status];
        if (s !== 0) return s;
        return b.version - a.version;
      })
      .map((r) => ({
        value: `${r.canonical_type}|${r.version}`,
        label: `${r.canonical_type} v${r.version}`,
        description: r.status,
      }));
  }, [canonicalQuery.data]);

  const [canonicalModel, canonicalVersion] = React.useMemo<[string, number]>(() => {
    if (!canonicalKey) return ["", 0];
    const [m, v] = canonicalKey.split("|");
    return [m, Number(v) || 0];
  }, [canonicalKey]);

  const createMutation = useMutation({
    mutationFn: () =>
      workflowsApi.create(
        isCanonical
          ? {
              name: name.trim(),
              kind: "canonical",
              direction,
              canonical_model: canonicalModel.trim(),
              canonical_version: canonicalVersion,
              description: description.trim() || null,
              tags,
            }
          : {
              name: name.trim(),
              kind: "orchestration",
              description: description.trim() || null,
              tags,
            },
      ),
    onSuccess: (wf) => {
      qc.invalidateQueries({ queryKey: ["workflows"] });
      onOpenChange(false);
      // Jump straight into the editor. Both kinds live under the unified
      // /workflows route now.
      nav(`/workflows/${wf.id}`);
    },
  });

  const canSubmit =
    name.trim().length > 0 &&
    (!isCanonical || (canonicalModel.length > 0 && canonicalVersion >= 1)) &&
    !createMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New workflow</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {/* Kind picker — the one decision that branches the whole form.
              Canonical binds to a model+direction (immutable after create);
              orchestration is free-form (n8n-style), no canonical fields. */}
          <div className="space-y-1.5">
            <Label>Kind</Label>
            <div className="grid grid-cols-2 gap-2">
              {([
                {
                  k: "canonical" as const,
                  title: "Canonical",
                  hint: "Bound to a canonical model + direction",
                },
                {
                  k: "orchestration" as const,
                  title: "Orchestration",
                  hint: "Free triggers & sinks, no canonical",
                },
              ]).map((opt) => {
                const on = selectedKind === opt.k;
                return (
                  <button
                    key={opt.k}
                    type="button"
                    onClick={() => setSelectedKind(opt.k)}
                    className={cn(
                      "rounded-lg border px-3 py-2 text-left transition",
                      on
                        ? "border-accent bg-accent/10 text-foreground"
                        : "border-border bg-surface text-muted-foreground hover:border-foreground/30 hover:text-foreground",
                    )}
                  >
                    <div className="text-sm font-medium">{opt.title}</div>
                    <div className="text-[11px] leading-tight text-muted-foreground">
                      {opt.hint}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="wf-name">Name</Label>
            <Input
              id="wf-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={
                isCanonical
                  ? "e.g. EDI Order inbound"
                  : "e.g. Nightly DB backup notifier"
              }
              autoFocus
            />
          </div>

          {/* Direction + canonical pickers are CANONICAL-only.
              Orchestration workflows have no canonical binding — they
              live in the n8n-style automation world. */}
          {isCanonical && (
            <>
              <div className="space-y-1.5">
                <Label>Direction</Label>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                  {DIRECTION_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setDirection(opt.value)}
                      className={
                        "flex flex-col items-start gap-1 border px-3 py-2 text-left text-xs transition " +
                        (direction === opt.value
                          ? "border-accent bg-accent/10 text-foreground"
                          : "border-border bg-surface text-muted-foreground hover:border-muted-foreground")
                      }
                    >
                      <span className="text-sm font-medium text-foreground">{opt.label}</span>
                      <span className="leading-snug">{opt.hint}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="wf-canonical">Canonical model</Label>
                <Combobox
                  id="wf-canonical"
                  options={canonicalOptions}
                  value={canonicalKey}
                  onChange={setCanonicalKey}
                  placeholder={canonicalQuery.isLoading ? "loading…" : "Pick a model"}
                  loading={canonicalQuery.isLoading}
                  emptyText="No canonical schemas — create one under /canonical first."
                />
                {canonicalOptions.length === 0 && !canonicalQuery.isLoading && (
                  <p className="text-xs text-muted-foreground">
                    No canonical schemas defined. Go to <span className="font-mono">/canonical</span> to create one.
                  </p>
                )}
              </div>
            </>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="wf-desc">Description (optional)</Label>
            <Input
              id="wf-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={
                isCanonical
                  ? "What does this workflow do?"
                  : "What does this automation do?"
              }
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="wf-tags">Tags (optional)</Label>
            <TagsField
              id="wf-tags"
              value={tags}
              onChange={setTags}
              scope="workflows"
              placeholder="e.g. customer:acme, env:prod"
            />
            <p className="text-xs text-muted-foreground">
              Organize workflows by customer, environment or domain. Use them later to filter lists and the topology view.
            </p>
          </div>

          {createMutation.isError && (
            <Alert tone="danger">
              {(createMutation.error as Error)?.message ?? "Create failed"}
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={createMutation.isPending}>
            Cancel
          </Button>
          <Button onClick={() => createMutation.mutate()} disabled={!canSubmit}>
            {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />}
            Create &amp; open editor
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
